/**
 * Синглтон Socket.IO клиента.
 * Один экземпляр на всё приложение — подключается один раз в SocketProvider
 * и используется через useSocket() хук в любом компоненте.
 */
import { io as ioClient, Socket } from 'socket.io-client'

let socket: Socket | null = null
let connectionPromise: Promise<Socket | null> | null = null

// Трекинг подписанных досок для переподписки при реконнекте
const joinedBoards = new Set<string>()

/**
 * Получить или создать единственный экземпляр Socket.IO подключения.
 * Авторизуется через /api/auth/ws-token (httpOnly cookie → token).
 */
export async function getSocket(): Promise<Socket | null> {
  // Если socket существует (даже если временно отключён) — возвращаем его.
  // Socket.IO сам обрабатывает реконнект.
  if (socket) {
    return socket
  }

  // Если уже в процессе подключения — ждём
  if (connectionPromise) {
    return connectionPromise
  }

  connectionPromise = connectSocket()
  return connectionPromise
}

async function connectSocket(): Promise<Socket | null> {
  try {
    // Получаем токен через серверный эндпоинт (httpOnly cookie)
    const basePath = process.env.NEXT_PUBLIC_BASE_PATH || ''
    const res = await fetch(`${basePath}/api/auth/ws-token`)

    if (!res.ok) {
      console.log('[SOCKET] No auth token, skipping connection')
      connectionPromise = null
      return null
    }

    const { token } = await res.json()

    if (!token) {
      console.log('[SOCKET] Empty token, skipping connection')
      connectionPromise = null
      return null
    }

    const url = window.location.origin

    console.log('[SOCKET] Connecting to:', url, 'path: /tasks/socket.io')

    socket = ioClient(url, {
      path: '/tasks/socket.io',
      auth: { token },
      transports: ['websocket'],
      upgrade: false,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 30000,
    })

    socket.on('connect', () => {
      console.log('[SOCKET] Connected! ID:', socket?.id, 'transport:', socket?.io?.engine?.transport?.name)
      // При каждом подключении/переподключении — переподписываемся на все доски
      rejoinAllBoards()
    })

    socket.on('connect_error', (err) => {
      // timeout и ping timeout — нормальные события при первом подключении через nginx
      if (err.message === 'timeout' || err.message === 'ping timeout') return
      console.error('[SOCKET] Connection error:', err.message)
    })

    socket.on('disconnect', (reason) => {
      console.log('[SOCKET] Disconnected:', reason)
    })

    // Ждём первое подключение
    await new Promise<void>((resolve) => {
      if (socket?.connected) {
        resolve()
        return
      }
      socket?.on('connect', () => resolve())
      // Если не подключится за 10с — всё равно резолвим
      setTimeout(() => resolve(), 10000)
    })

    return socket
  } catch (err) {
    console.error('[SOCKET] Failed to connect:', err)
    connectionPromise = null
    return null
  }
}

/**
 * Переподписаться на все доски при реконнекте.
 * При disconnect/reconnect сокет получает новый ID и теряет все комнаты.
 */
function rejoinAllBoards() {
  if (!socket?.connected || joinedBoards.size === 0) return
  console.log('[SOCKET] Rejoining boards after reconnect:', joinedBoards.size)
  for (const boardId of joinedBoards) {
    socket.emit('join-board', boardId)
  }
}

/**
 * Синхронно получить текущий экземпляр socket (без создания подключения).
 * Используется в useEffect для подписки на события без триггера ре-рендеров.
 */
export function getSocketSync(): Socket | null {
  return socket
}

/**
 * Отключить сокет (при логауте).
 */
export function disconnectSocket() {
  if (socket) {
    socket.disconnect()
    socket = null
    connectionPromise = null
    joinedBoards.clear()
  }
}

/**
 * Подписаться на комнату доски (вызывать при переходе на доску).
 */
export async function joinBoard(boardId: string) {
  console.log('[SOCKET] joinBoard called for:', boardId)
  const s = await getSocket()
  console.log('[SOCKET] joinBoard getSocket returned, connected:', s?.connected, 'id:', s?.id)

  // Сохраняем в трекер — для переподписки при реконнекте
  joinedBoards.add(boardId)

  if (s?.connected) {
    s.emit('join-board', boardId)
    console.log('[SOCKET] join-board emitted for:', boardId)
  } else {
    console.warn('[SOCKET] joinBoard: socket not connected, queuing join for:', boardId)
    // Если сокет ещё не подключён, подписываемся на событие connect
    // rejoinAllBoards() вызовется автоматически при connect
  }
}

/**
 * Покинуть комнату доски (вызывать при уходе с доски).
 */
export async function leaveBoard(boardId: string) {
  console.log('[SOCKET] leaveBoard called for:', boardId)
  const s = await getSocket()
  // Убираем из трекера
  joinedBoards.delete(boardId)
  if (s?.connected) {
    s.emit('leave-board', boardId)
    console.log('[SOCKET] leave-board emitted for:', boardId)
  }
}
